'use client';
import React, { useRef, createContext, useContext, useEffect } from 'react';
import { OverlayPanel } from 'primereact/overlaypanel';
import { useTranslation } from 'react-i18next';
import {
  DEFAULT_LANGUAGE,
  normalizeLanguage,
} from 'features/language/languageConfig';

const DropdownContext = createContext<(() => void) | null>(null);
const openDropdowns = new Set<() => void>();

type MintHrDropdownProps = {
  title: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  buttonClassName?: string;
  onItemSelect?: (selectedItem: string) => void;
  style?: React.CSSProperties;
  titleClassName?: string;
  maxHeight?: number | string;
  OverlayPanelClassName?: string;
};

type ChildWithOnSelect = React.ReactElement<{
  onSelect?: (value: string) => void;
}>;

const MintHrDropdown = ({
  title,
  children,
  className = '',
  buttonClassName = '',
  onItemSelect,
  style,
  titleClassName,
  maxHeight,
  OverlayPanelClassName,
}: MintHrDropdownProps) => {
  const op = useRef<OverlayPanel>(null);

  const { i18n } = useTranslation(undefined, { useSuspense: false });
  const isRTL = (normalizeLanguage(i18n.language) ?? DEFAULT_LANGUAGE) === 'ar';

  const closeDropdown = () => {
    op.current?.hide();
    openDropdowns.delete(closeDropdown);
  };

  const handleSelect = (value: string) => {
    if (onItemSelect) onItemSelect(value);
    closeDropdown();
  };

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    openDropdowns.forEach((close) => close());
    openDropdowns.clear();
    op.current?.toggle(e);

    setTimeout(() => {
      if (op.current?.isVisible?.()) {
        openDropdowns.add(closeDropdown);
      }
    }, 0);
  };
  useEffect(() => {
    return () => {
      openDropdowns.delete(closeDropdown);
    };
  }, []);

  return (
    <div
      className={`minthr-dropdown ${titleClassName ?? 'minthr-ml-auto'} ${className}`}
      style={style}
    >
      <div className={`${buttonClassName}`} onClick={handleClick}>
        {title}
      </div>

      <OverlayPanel
        ref={op}
        style={{
          position: 'absolute',
          direction: isRTL ? 'rtl' : 'ltr',
          maxHeight,
          overflowY: maxHeight ? 'auto' : undefined,
        }}
        className={`dropdown minthr-dropdown ${OverlayPanelClassName}`}
      >
        <DropdownContext.Provider value={closeDropdown}>
          <div
            onClick={(e) => e.stopPropagation()}
            className="dropdown-menu show "
          >
            {React.Children.map(children, (child) =>
              React.isValidElement(child)
                ? React.cloneElement(child as ChildWithOnSelect, {
                    onSelect: handleSelect,
                  })
                : child,
            )}
          </div>
        </DropdownContext.Provider>
      </OverlayPanel>
    </div>
  );
};

type MintHrDropdownItemProps = {
  children: React.ReactNode;
  onClick?: () => void;
  className?: string;
  value?: string;
  id?: string;
  hidden?: boolean;
  style?: React.CSSProperties;
  onSelect?: (value: string) => void;
};

const MintHrDropdownItem = ({
  children,
  onClick,
  className = '',
  value = '',
  id,
  hidden = false,
  style,
  onSelect,
}: MintHrDropdownItemProps) => {
  const closeDropdown = useContext(DropdownContext);

  if (hidden) return null;

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (onClick) {
      onClick();
    }
    if (onSelect) {
      onSelect(value);
    }
    if (closeDropdown) {
      closeDropdown();
    }
  };

  return (
    <a
      className={`dropdown-item  ${className}`}
      id={id}
      style={style}
      onClick={handleClick}
    >
      {children}
    </a>
  );
};

export { MintHrDropdown, MintHrDropdownItem };
